import { apiRequest } from '../api';

export interface ProgramDraft {
  _id: string;
  programData: {
    title: string;
    subtitle?: string;
    videoUrl?: string;
    language?: 'english' | 'spanish';
    programType?: 'hybrid' | 'on-Campus' | 'via-zoom' | 'online';
    status?: 'draft';
    tagline?: string;
    about?: string;
    categoryId?: string;
    whoisthisFor?: any[];
    wherecaniWork?: any[];
    keyFeatures?: any[];
    thisCourseData?: any[];
    clinicalProcedures?: any[];
    clinicalProceduresGrouped?: Array<{
      label?: string;
      subHeading?: string;
      items: Array<{ id: string; title: string }>;
    }>;
    nationalCertifications?: {
      isallIncluded: boolean;
      items: any[];
    };
    faqs?: any[];
    podcastData?: any[];
    breadcrumb?: any[];
    programComparison?: {
      showComparisonTable: boolean;
      comparisonCategories: any[];
    };
    premiumDescription?: string;
    premiumRating?: number;
    premiumReviewCount?: number;
    premiumStudentCount?: number;
    premiumViewCount?: number;
    premiumLikeCount?: number;
    premiumShareCount?: number;
    payAsYouGo?: boolean;
    modules?: Array<{
      label: string;
      items: Array<{
        id: string;
        title: string;
        subtitle?: string;
        isExam?: boolean;
      }>;
    }>;
    modulePricingMetadata?: {
      totalModules?: number;
      totalPrice?: number;
      averageModulePrice?: number;
      bundlePrice?: number;
      lastPricingUpdate?: string;
    };
    socialMedia?: {
      facebook?: string | null;
      twitter?: string | null;
      instagram?: string | null;
      linkedin?: string | null;
      youtube?: string | null;
      website?: string | null;
    };
  };
  version: number;
  createdBy: {
    id: string;
    name: string;
  };
  lastModified: string;
  createdAt: string;
  updatedAt: string;
}

export interface DraftResponse {
  statusCode: number;
  message: string;
  data?: {
    drafts?: ProgramDraft[];
    draft?: ProgramDraft;
  };
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export interface CreateProgramData {
  title: string;
  subtitle: string;
  videoUrl: string;
  language: "english" | "spanish";
  status: "active" | "inactive" | "draft";
  tagline?: string;
  socialMedia?: {
    facebook?: string | null;
    twitter?: string | null;
    instagram?: string | null;
    linkedin?: string | null;
    youtube?: string | null;
    website?: string | null;
  };
  categoryId: string;
  about: string;
  modules: Array<{
    label: string;
    price?: number;
    isActive?: boolean;
    order?: number;
  }>;
  keyFeatures: Array<{
    id: number;
    name: string;
    icon: string;
    value: string;
    description: string; // Rich text support, 10-10,000 characters
  }>;
  breadcrumb: Array<{
    label: string;
    href: string;
  }>;
  thisCourseData?: Array<{
    id: number;
    name: string;
  }>;
  whoisthisFor?: Array<{
    id: number;
    category: string;
    title: string;
    description: string;
  }>;
  wherecaniWork?: Array<{
    id: number;
    icon: string;
    title: string;
    description: string;
  }>;
  clinicalProcedures?: Array<{
    id: string;
    title: string;
  }>;
  clinicalProceduresGrouped?: Array<{
    label?: string;
    subHeading?: string;
    items: Array<{ id: string; title: string }>;
  }>;
  nationalCertifications?: {
    isallIncluded: boolean;
    items: string[];
  };
  workPlaces?: string[];
  faqs?: Array<{
    id: number;
    type: string;
    question: string;
    answer: string;
  }>;
  podcastData?: Array<{
    id: string;
    title: string;
    category: string;
    duration: string;
    imageUrl: string;
    audioUrl: string;
  }>;
  programComparison?: {
    showComparisonTable: boolean;
    comparisonCategories: Array<{
      name: string;
      features: Array<{
        name: string;
        included: boolean;
        includedInOther: boolean;
        otherNote?: string;
      }>;
    }>;
  };
  defaultProgramType: string;
  programTypeDataMap?: any;
  typeSpecificData?: any;
  payAsYouGo?: boolean;
  // ❌ Remove modulePricingEnabled - it's no longer required by the backend
  moduleConfiguration?: any;
  modulePricingMetadata?: any;
  // Duration field
  duration?: {
    value: number;
    isInWeeks: boolean;
  };
  // Platform configuration
  platform?: {
    isIspring?: boolean;
    contentTitle?: string;
    contentItemId?: string;
  };
  
  // Premium Fields - Optional analytics and premium indicators
  premiumDescription?: string;
  premiumTagline?: string;
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
  premiumShareCount?: number;
}

export interface ProgramResponse {
  success: boolean;
  message: string;
  data?: {
    program: {
      id: string;
      programId: string;
      title: string;
      status: string;
      payAsYouGo?: boolean;
      // ❌ Remove modulePricingEnabled - it's no longer required by the backend
      moduleCount?: number;
      baseModulesCount?: number;
      moduleConfigurationCount?: number;
      unifiedModulesCount?: number;
      createdAt: string;
    };
    modules: Array<{
      id: string;
      label: string;
      price: number;
      isActive: boolean;
      order: number;
    }>;
  };
  errors?: Array<{
    code: string;
    expected?: any;
    received?: any;
    path: string[];
    message: string;
  }>;
}

export interface ProgramsResponse {
  statusCode: number;
  message: string;
  data: {
    programs: Array<{
      programId: string;
      title: string;
      subtitle: string;
      language: string;
      status: string;
      createdAt: string;
      updatedAt: string;
      // New module-related fields
      payAsYouGo?: boolean;
      moduleConfiguration?: {
        payAsYouGo: boolean;
        // ❌ Remove modulePricingEnabled - it's no longer required by the backend
        modules: Array<{
          _id?: string;
          name: string;
          description?: string;
          programId?: string;
          price: number;
          isActive: boolean;
          order: number;
          metadata?: {
            difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
            tags?: string[];
          };
          prerequisites?: string[];
          estimatedCompletionTime?: number;
          isExam?: boolean;
          examDetails?: {
            passingScore: number;
            timeLimit?: number;
            questions?: number;
          };
        }>;
        pricingStrategy?: 'INDIVIDUAL' | 'BUNDLE';
        bundleDiscount?: number;
        minimumModulesForBundle?: number;
      };
      modulePricingMetadata?: {
        totalModules: number;
        totalPrice: number;
        averageModulePrice: number;
        bundlePrice?: number;
        savingsWithBundle?: number;
      };
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

// Updated program API functions to match new API structure
export async function fetchAllPrograms(params: {
  categoryId?: string;
  programType?: string;
  status?: string;
  language?: string;
  search?: string;
  page?: number;
  limit?: number;
} = {}): Promise<{
  success: boolean;
  message: string;
  data: {
    programs: Array<{
      id: string;
      programId: string;
      title: string;
      description?: string;
      category?: string;
      status: string;
      price?: number;
      duration?: string;
      level?: string;
      language?: string;
      instructor?: string;
      thumbnail?: string;
      banner?: string;
      videoUrl?: string;
      slug?: string;
      payAsYouGo?: boolean;
      // ❌ Remove modulePricingEnabled - it's no longer required by the backend
      moduleCount?: number;
      features?: string[];
      learningOutcomes?: string[];
      createdAt: string;
      updatedAt?: string;
    }>;
    total: number;
    page: number;
    limit: number;
  };
}> {
  const queryParams = new URLSearchParams();
  
  if (params.categoryId) queryParams.append('categoryId', params.categoryId);
  if (params.programType) queryParams.append('programType', params.programType);
  if (params.status) queryParams.append('status', params.status);
  if (params.language) queryParams.append('language', params.language);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  
  const url = `/api/programs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return apiRequest(url);
}

export async function fetchAllProgramsAdmin(params: {
  categoryId?: string;
  programType?: string;
  status?: string;
  language?: string;
  search?: string;
  page?: number;
  limit?: number;
} = {}): Promise<{
  success: boolean;
  message: string;
  data: {
    programs: Array<{
      id: string;
      programId: string;
      title: string;
      status: string;
      price?: number;
      moduleCount?: number;
      payAsYouGo?: boolean;
      // ❌ Remove modulePricingEnabled - it's no longer required by the backend
      createdAt: string;
      updatedAt?: string;
    }>;
    total: number;
    page: number;
    limit: number;
  };
}> {
  const queryParams = new URLSearchParams();
  
  if (params.categoryId) queryParams.append('categoryId', params.categoryId);
  if (params.programType) queryParams.append('programType', params.programType);
  if (params.status) queryParams.append('status', params.status);
  if (params.language) queryParams.append('language', params.language);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  
  const url = `/api/programs/admin${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return apiRequest(url);
}

export async function fetchProgramById(id: string): Promise<{
  success: boolean;
  message: string;
  data?: {
    program: {
      id: string;
      programId: string;
      title: string;
      description?: string;
      category?: string;
      status: string;
      price?: number;
      duration?: string;
      level?: string;
      language?: string;
      instructor?: string;
      thumbnail?: string;
      banner?: string;
      videoUrl?: string;
      slug?: string;
      payAsYouGo?: boolean;
      // ❌ Remove modulePricingEnabled - it's no longer required by the backend
      moduleCount?: number;
      baseModulesCount?: number;
      moduleConfigurationCount?: number;
      unifiedModulesCount?: number;
      features?: string[];
      learningOutcomes?: string[];
      prerequisites?: string[];
      faqs?: Array<{
        id: number;
        type: string;
        question: string;
        answer: string;
      }>;
      clinicalProcedures?: Array<{
        id: string;
        title: string;
      }>;
      nationalCertifications?: {
        isallIncluded: boolean;
        items: string[];
      };
      createdAt: string;
      updatedAt?: string;
    };
    modules: Array<{
      id: string;
      label: string;
      price: number;
      isActive: boolean;
      order: number;
    }>;
  };
  errors?: Array<{
    code: string;
    expected?: any;
    received?: any;
    path: string[];
    message: string;
  }>;
}> {
  return apiRequest(`/api/programs/admin/${id}`);
}

export async function createProgram(programData: CreateProgramData): Promise<ProgramResponse> {
  return apiRequest('/api/programs/admin', { method: 'POST', body: programData });
}

export async function updateProgram(id: string, programData: Partial<CreateProgramData>): Promise<ProgramResponse> {
  return apiRequest(`/api/programs/admin/${id}`, { method: 'PUT', body: programData });
}

export async function deleteProgram(id: string): Promise<{ statusCode: number; message: string; data: { id: string } }> {
  return apiRequest<{ statusCode: number; message: string; data: { id: string } }>(`/api/programs/admin/${id}`, {
    method: 'DELETE'
  });
}

// Program drafts API functions
export async function fetchAllDrafts(): Promise<DraftResponse> {
  return apiRequest<DraftResponse>('/api/programs/drafts', {
    method: 'GET'
  });
}

export async function fetchDraftById(draftId: string): Promise<DraftResponse> {
  return apiRequest<DraftResponse>(`/api/programs/drafts/${draftId}`, {
    method: 'GET'
  });
}

export async function createDraft(programData: any): Promise<DraftResponse> {
  return apiRequest<DraftResponse>('/api/programs/drafts', {
    method: 'POST',
    body: { programData }
  });
}

export async function updateDraft(draftId: string, programData: any): Promise<DraftResponse> {
  return apiRequest<DraftResponse>(`/api/programs/drafts/${draftId}`, {
    method: 'PUT',
    body: { programData }
  });
}

export async function deleteDraft(draftId: string): Promise<{ statusCode: number; message: string }> {
  return apiRequest<{ statusCode: number; message: string }>(`/api/programs/drafts/${draftId}`, {
    method: 'DELETE'
  });
}

export async function bulkDeleteDrafts(draftIds: string[]): Promise<{
  statusCode: number;
  message: string;
  data: {
    bulkDelete: {
      totalRequested: number;
      totalFound: number;
      totalDeleted: number;
      notFoundIds: string[];
      deletedIds: string[];
    };
  };
}> {
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      bulkDelete: {
        totalRequested: number;
        totalFound: number;
        totalDeleted: number;
        notFoundIds: string[];
        deletedIds: string[];
      };
    };
  }>('/api/programs/drafts/bulk-delete', {
    method: 'DELETE',
    body: { draftIds }
  });
}

export async function duplicateDraft(draftId: string, title?: string): Promise<DraftResponse> {
  return apiRequest<DraftResponse>(`/api/programs/drafts/${draftId}/duplicate`, {
    method: 'POST',
    body: title ? { title } : {}
  });
}

export async function getDraftHistory(draftId: string): Promise<{
  statusCode: number;
  message: string;
  data: {
    history: Array<{
      version: number;
      lastModified: string;
      changes: string[];
    }>;
  };
}> {
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      history: Array<{
        version: number;
        lastModified: string;
        changes: string[];
      }>;
    };
  }>(`/api/programs/drafts/${draftId}/history`, {
    method: 'GET'
  });
}

export async function createProgramFromDraft(draftId: string): Promise<ProgramResponse> {
  return apiRequest<ProgramResponse>(`/api/programs/drafts/${draftId}/create-program`, {
    method: 'POST'
  });
}

export async function getDraft(draftId: string): Promise<DraftResponse> {
  return apiRequest<DraftResponse>(`/api/programs/drafts/${draftId}`, {
    method: 'GET'
  });
}

export async function updateModuleConfiguration(programId: string, moduleConfiguration: any): Promise<ProgramResponse> {
  return apiRequest(`/api/programs/admin/${programId}`, { method: 'PUT', body: moduleConfiguration });
}

// Export all program API functions as a single object
export const programApi = {
  fetchAllPrograms,
  fetchProgramById,
  createProgram,
  updateProgram,
  deleteProgram,
  fetchAllDrafts,
  fetchDraftById,
  createDraft,
  updateDraft,
  deleteDraft,
  bulkDeleteDrafts,
  duplicateDraft,
  getDraftHistory,
  createProgramFromDraft,
  getDraft,
  updateModuleConfiguration
}; 